import { Alert, Progress } from 'antd'
import { useBatchStatusQuery } from '../../../domain/invoicing/invoicingQueries'
import { BatchType } from '../../../domain/invoicing/invoicingApi'
import { isBatchProcessing, calculateBatchProgress } from './batchInvoicingModel'
import { getBatchTypeCopy } from '../batchTypeCopy'

type Props = {
  batchId: string
  onDismiss: () => void
  batchType: BatchType
}

const BatchProgressBanner = ({ batchId, onDismiss, batchType }: Props) => {
  const copy = getBatchTypeCopy(batchType)
  const { data: status } = useBatchStatusQuery(batchId)

  if (!status) {
    return null
  }

  const isProcessing = isBatchProcessing(status.status)
  const isCompleted = status.status === 'completed'
  const isFailed = status.status === 'failed'
  const skippedJobs = status.skippedJobs ?? 0
  const percent = calculateBatchProgress(status.completedJobs, skippedJobs, status.failedJobs, status.totalJobs)

  if (isProcessing) {
    return (
      <Alert
        type="info"
        showIcon
        message={copy.processingTitle}
        description={
          <div>
            <div>{status.completedJobs + skippedJobs + status.failedJobs} of {status.totalJobs} {copy.itemNoun} processed</div>
            <Progress percent={percent} status="active" size="small" />
          </div>
        }
        style={{ marginBottom: 16 }}
      />
    )
  }

  if (isCompleted) {
    const errorJobs = status.jobs.filter(j => j.messageType === 'error')
    const warningJobs = status.jobs.filter(j => j.messageType === 'warning')
    const hasErrors = errorJobs.length > 0
    const hasWarnings = warningJobs.length > 0

    if (!hasErrors && !hasWarnings) {
      const skippedSuffix = skippedJobs > 0
        ? ` ${skippedJobs} job${skippedJobs === 1 ? '' : 's'} skipped.`
        : ''
      return (
        <Alert
          type="success"
          showIcon
          closable
          onClose={onDismiss}
          message={copy.completedTitle}
          description={`${copy.successDescriptionPrefix} ${status.completedJobs} invoice${status.completedJobs === 1 ? '' : 's'}.${skippedSuffix}`}
          style={{ marginBottom: 16 }}
        />
      )
    }

    return (
      <Alert
        type="warning"
        showIcon
        closable
        onClose={onDismiss}
        message={hasErrors ? copy.completedWithErrorsTitle : copy.completedWithWarningsTitle}
        description={
          <div>
            <div>
              {status.completedJobs} succeeded, {skippedJobs > 0 ? `${skippedJobs} skipped, ` : ''}{status.failedJobs} failed.
            </div>
            {errorJobs.length > 0 && (
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                {errorJobs.slice(0, 5).map(j => (
                  <li key={j.id} style={{ color: '#ff4d4f' }}>
                    {j.customerName} - {j.jobName}: {j.message}
                  </li>
                ))}
                {errorJobs.length > 5 && <li>...and {errorJobs.length - 5} more</li>}
              </ul>
            )}
            {warningJobs.length > 0 && (
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                {warningJobs.slice(0, 5).map(j => (
                  <li key={j.id} style={{ color: '#faad14' }}>
                    {j.customerName} - {j.jobName}: {j.message}
                  </li>
                ))}
                {warningJobs.length > 5 && <li>...and {warningJobs.length - 5} more</li>}
              </ul>
            )}
          </div>
        }
        style={{ marginBottom: 16 }}
      />
    )
  }

  if (isFailed) {
    const errorJobs = status.jobs.filter(j => j.messageType === 'error')
    return (
      <Alert
        type="error"
        showIcon
        closable
        onClose={onDismiss}
        message={copy.failedTitle}
        description={
          <div>
            <div>All {status.totalJobs} {copy.itemNoun} failed.</div>
            {errorJobs.length > 0 && (
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                {errorJobs.slice(0, 5).map(j => (
                  <li key={j.id}>
                    {j.customerName} - {j.jobName}: {j.message}
                  </li>
                ))}
                {errorJobs.length > 5 && <li>...and {errorJobs.length - 5} more</li>}
              </ul>
            )}
          </div>
        }
        style={{ marginBottom: 16 }}
      />
    )
  }

  return null
}

export default BatchProgressBanner
